// src/components/whiteboard/WhiteboardCanvas.js
import React, { useRef, useEffect, useState } from 'react';
import { fabric } from 'fabric';
import styled from 'styled-components';
import { useClassroom } from '../../contexts/ClassroomContext';
import { supabase } from '../../services/supabase';
import { Calculator, Grid, Square, Circle, Triangle, Play, Pause, TrendingUp } from 'lucide-react';

const CanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: white;
`;

const MathToolbar = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 8px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: ${props => props.active ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#374151'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#e5e7eb'};
  }
`;

const FunctionInput = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 300px;
`;

const InputField = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FunctionButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 8px;

  &:hover {
    background: #059669;
  }
`;

const CoordinateSystem = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 200px;
  height: 150px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  padding: 10px;
  z-index: 1000;
`;

const TeachingMaterialsPanel = styled.div`
  position: absolute;
  left: 10px;
  bottom: 10px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-width: 250px;
`;

const MaterialCategory = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const MaterialItem = styled.button`
  display: block;
  width: 100%;
  padding: 6px 8px;
  margin: 2px 0;
  border: none;
  background: #f3f4f6;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  text-align: left;

  &:hover {
    background: #e5e7eb;
  }
`;

const WhiteboardCanvas = ({ currentTool }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const { whiteboardData, setWhiteboardData, roomId } = useClassroom();
  const [isDrawing, setIsDrawing] = useState(false);
  const [activeMathTool, setActiveMathTool] = useState('pen');
  const [functionInput, setFunctionInput] = useState('');
  const [showCoordinateSystem, setShowCoordinateSystem] = useState(false);
  const [showTeachingMaterials, setShowTeachingMaterials] = useState(false);
  const [plottedFunctions, setPlottedFunctions] = useState([]);

  useEffect(() => {
    // Initialize Fabric.js canvas
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: window.innerWidth - 300,
      height: window.innerHeight - 200,
      backgroundColor: 'white',
      selection: currentTool === 'select'
    });

    fabricCanvasRef.current = canvas;

    // Set drawing mode based on current tool
    canvas.isDrawingMode = currentTool === 'pen' || currentTool === 'eraser';
    
    if (canvas.isDrawingMode) {
      canvas.freeDrawingBrush.width = currentTool === 'pen' ? 2 : 10;
      canvas.freeDrawingBrush.color = currentTool === 'pen' ? 'black' : 'white';
    }

    // Handle canvas events
    canvas.on('path:created', handlePathCreated);
    canvas.on('object:added', handleObjectAdded);
    canvas.on('object:modified', handleObjectModified);

    // Load existing whiteboard data
    loadWhiteboardData();

    return () => {
      canvas.dispose();
    };
  }, [currentTool, roomId]);

  const handlePathCreated = (e) => {
    saveCanvasState();
  };

  const handleObjectAdded = (e) => {
    if (e.target && !e.target.fromLoad) {
      saveCanvasState();
    }
  };

  const handleObjectModified = (e) => {
    saveCanvasState();
  };

  const saveCanvasState = async () => {
    if (!fabricCanvasRef.current || !roomId) return;
    
    const canvasData = JSON.stringify(fabricCanvasRef.current.toJSON());
    
    try {
      await supabase
        .from('myclassboard_whiteboard_data')
        .upsert({
          room_id: roomId,
          canvas_data: canvasData,
          updated_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('Error saving whiteboard data:', error);
    }
  };

  const loadWhiteboardData = async () => {
    if (!roomId) return;
    
    try {
      const { data, error } = await supabase
        .from('myclassboard_whiteboard_data')
        .select('canvas_data')
        .eq('room_id', roomId)
        .single();
      
      if (data && data.canvas_data) {
        fabricCanvasRef.current.loadFromJSON(data.canvas_data, () => {
          fabricCanvasRef.current.renderAll();
        });
      }
    } catch (error) {
      console.error('Error loading whiteboard data:', error);
    }
  };

  const addText = (text, x = 100, y = 100) => {
    const textObj = new fabric.IText(text, {
      left: x,
      top: y,
      fontFamily: 'Arial',
      fontSize: 20,
      fill: 'black'
    });
    
    fabricCanvasRef.current.add(textObj);
    fabricCanvasRef.current.setActiveObject(textObj);
  };

  const addImage = (imageUrl) => {
    fabric.Image.fromURL(imageUrl, (img) => {
      img.set({
        left: 100,
        top: 100,
        scaleX: 0.5,
        scaleY: 0.5
      });
      fabricCanvasRef.current.add(img);
    });
  };

  const clearCanvas = () => {
    fabricCanvasRef.current.clear();
    fabricCanvasRef.current.backgroundColor = 'white';
    saveCanvasState();
  };

  // Mathematical function plotting
  const plotFunction = (funcString) => {
    if (!fabricCanvasRef.current) return;

    try {
      // Simple function parser for basic mathematical functions
      const canvas = fabricCanvasRef.current;
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const scale = 50; // pixels per unit

      // Parse and evaluate function
      const points = [];
      for (let x = -10; x <= 10; x += 0.1) {
        try {
          let y;
          // Handle common function types
          if (funcString.includes('x^2') || funcString.includes('x²')) {
            y = x * x;
          } else if (funcString.includes('sin')) {
            y = Math.sin(x);
          } else if (funcString.includes('cos')) {
            y = Math.cos(x);
          } else if (funcString.includes('x^3') || funcString.includes('x³')) {
            y = x * x * x;
          } else if (funcString.includes('sqrt') || funcString.includes('√')) {
            y = Math.sqrt(Math.abs(x));
          } else if (funcString.includes('log')) {
            y = Math.log(Math.abs(x));
          } else {
            // Simple linear function: y = x
            y = x;
          }

          const canvasX = centerX + (x * scale);
          const canvasY = centerY - (y * scale);

          if (canvasX >= 0 && canvasX <= width && canvasY >= 0 && canvasY <= height) {
            points.push({ x: canvasX, y: canvasY });
          }
        } catch (e) {
          console.warn('Error evaluating function at x =', x, e);
        }
      }

      // Create path for the function
      if (points.length > 1) {
        const pathString = points.map((point, index) => 
          `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
        ).join(' ');

        const path = new fabric.Path(pathString, {
          stroke: '#ef4444',
          strokeWidth: 2,
          fill: '',
          selectable: true,
          evented: true
        });

        canvas.add(path);
        canvas.renderAll();
      }
    } catch (error) {
      console.error('Error plotting function:', error);
    }
  };

  // Add geometric shapes
  const addGeometricShape = (shape) => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    let shapeObject;

    switch (shape) {
      case 'square':
        shapeObject = new fabric.Rect({
          left: 100,
          top: 100,
          width: 100,
          height: 100,
          fill: 'transparent',
          stroke: '#3b82f6',
          strokeWidth: 2
        });
        break;
      case 'circle':
        shapeObject = new fabric.Circle({
          left: 100,
          top: 100,
          radius: 50,
          fill: 'transparent',
          stroke: '#10b981',
          strokeWidth: 2
        });
        break;
      case 'triangle':
        shapeObject = new fabric.Triangle({
          left: 100,
          top: 100,
          width: 100,
          height: 100,
          fill: 'transparent',
          stroke: '#f59e0b',
          strokeWidth: 2
        });
        break;
      default:
        return;
    }

    canvas.add(shapeObject);
    canvas.renderAll();
  };

  // Add coordinate system
  const addCoordinateSystem = () => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;

    // X-axis
    const xAxis = new fabric.Line([0, centerY, width, centerY], {
      stroke: '#6b7280',
      strokeWidth: 1,
      selectable: false,
      evented: false
    });

    // Y-axis
    const yAxis = new fabric.Line([centerX, 0, centerX, height], {
      stroke: '#6b7280',
      strokeWidth: 1,
      selectable: false,
      evented: false
    });

    canvas.add(xAxis, yAxis);
    canvas.renderAll();
  };

  // Handle function input
  const handlePlotFunction = () => {
    if (functionInput.trim()) {
      plotFunction(functionInput);
      setPlottedFunctions(prev => [...prev, functionInput]);
      setFunctionInput('');
    }
  };

  // Teaching materials data
  const teachingMaterials = {
    '数学': ['二次函数', '三角函数', '几何图形', '坐标系', '函数图像'],
    '物理': ['力学', '电学', '光学', '热学', '波动'],
    '化学': ['元素周期表', '化学反应', '分子结构', '化学键', '酸碱'],
    '生物': ['细胞结构', '遗传学', '生态学', '进化论', '解剖学']
  };

  // Expose methods for toolbar
  useEffect(() => {
    window.whiteboardMethods = {
      addText,
      addImage,
      clearCanvas
    };
  }, []);

  return (
    <CanvasContainer>
      <canvas ref={canvasRef} />
      
      {/* Mathematical Tools Toolbar */}
      <MathToolbar>
        <ToolButton 
          active={activeMathTool === 'pen'} 
          onClick={() => setActiveMathTool('pen')}
          title="Pen Tool"
        >
          ✏️
        </ToolButton>
        <ToolButton 
          active={activeMathTool === 'function'} 
          onClick={() => setActiveMathTool('function')}
          title="Function Tool"
        >
          <TrendingUp size={20} />
        </ToolButton>
        <ToolButton 
          active={activeMathTool === 'grid'} 
          onClick={() => {
            setActiveMathTool('grid');
            addCoordinateSystem();
          }}
          title="Coordinate System"
        >
          <Grid size={20} />
        </ToolButton>
        <ToolButton 
          active={activeMathTool === 'calculator'} 
          onClick={() => setActiveMathTool('calculator')}
          title="Calculator"
        >
          <Calculator size={20} />
        </ToolButton>
      </MathToolbar>

      {/* Function Input Panel */}
      {activeMathTool === 'function' && (
        <FunctionInput>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#374151' }}>
            AI函数图像 (AI Function Graphing)
          </h4>
          <InputField
            type="text"
            placeholder="Enter function (e.g., x^2, sin(x), sqrt(x))"
            value={functionInput}
            onChange={(e) => setFunctionInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePlotFunction()}
          />
          <FunctionButton onClick={handlePlotFunction}>
            Plot Function
          </FunctionButton>
          <FunctionButton 
            onClick={() => setFunctionInput('')}
            style={{ background: '#6b7280' }}
          >
            Clear
          </FunctionButton>
        </FunctionInput>
      )}

      {/* Teaching Materials Panel */}
      {showTeachingMaterials && (
        <TeachingMaterialsPanel>
          <MaterialCategory>教学素材库 (Teaching Materials)</MaterialCategory>
          {Object.entries(teachingMaterials).map(([subject, materials]) => (
            <div key={subject}>
              <MaterialCategory>{subject}</MaterialCategory>
              {materials.map((material, index) => (
                <MaterialItem 
                  key={index}
                  onClick={() => {
                    // Add material to canvas
                    addText(material);
                  }}
                >
                  {material}
                </MaterialItem>
              ))}
            </div>
          ))}
        </TeachingMaterialsPanel>
      )}

      {/* Coordinate System Display */}
      {showCoordinateSystem && (
        <CoordinateSystem>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>坐标系 (Coordinate System)</h4>
          <div style={{ 
            width: '100%', 
            height: '100px', 
            border: '1px solid #d1d5db',
            position: 'relative',
            background: 'white'
          }}>
            {/* Simple coordinate system visualization */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '100%',
              height: '1px',
              background: '#6b7280',
              transform: 'translateY(-50%)'
            }}></div>
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '1px',
              height: '100%',
              background: '#6b7280',
              transform: 'translateX(-50%)'
            }}></div>
          </div>
        </CoordinateSystem>
      )}

      {/* Bottom Controls */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        display: 'flex',
        gap: '8px',
        zIndex: 1000
      }}>
        <ToolButton 
          onClick={() => setShowTeachingMaterials(!showTeachingMaterials)}
          title="Teaching Materials"
        >
          📚
        </ToolButton>
        <ToolButton 
          onClick={() => setShowCoordinateSystem(!showCoordinateSystem)}
          title="Coordinate System"
        >
          📐
        </ToolButton>
        <ToolButton 
          onClick={() => addGeometricShape('square')}
          title="Add Square"
        >
          <Square size={20} />
        </ToolButton>
        <ToolButton 
          onClick={() => addGeometricShape('circle')}
          title="Add Circle"
        >
          <Circle size={20} />
        </ToolButton>
        <ToolButton 
          onClick={() => addGeometricShape('triangle')}
          title="Add Triangle"
        >
          <Triangle size={20} />
        </ToolButton>
      </div>
    </CanvasContainer>
  );
};

export default WhiteboardCanvas;